<template>
    <slot
        name="default"
        :status
        :result="bundleResult"
        :load-more="loadMore"
        :total-results="totalResults"
        :last-page-number="lastPageNumber"
        :current-page-number="currentPage"
    />
</template>

<script setup lang="ts" generic="ResT extends QueryBundleResponse">
import type { QueryBundleProps, QueryBundleResponse, BundleFilters } from '~/types/bundle';

type BundleType = 'artists' | 'eventContents' | 'eventContentHighlight' | 'festivals' | 'productionHouses' | 'venues';

const ENDPOINTS: Record<BundleType, string> = {
    artists: '/v2.0/artists',
    eventContents: '/v2.0/event-contents',
    eventContentHighlight: '/v2.0/event-contents/highlight',
    festivals: '/v2.0/festivals',
    productionHouses: '/v2.0/production-houses',
    venues: '/v2.0/venues'
};

const props = withDefaults(
    defineProps<QueryBundleProps & { bundleType: BundleType }>(),
    {
        lazy: true,
        limit: 6,
    }
);

const {
    preset,
    artist,
    curator,
    discipline,
    events,
    productionHouse,
    purview,
    relatedToArtist,
    relatedToVenue,
    venue,
    search,
    skip,
    tag,
} = toRefs(props);

const currentPage = ref(1);

const totalResults = ref(0);
const lastPageNumber = ref(1);

const bundleResult: Ref<ResT['data'] | null> = ref(null);

const query = computed(() => {
    const res: BundleFilters = {
        page: currentPage.value,
        limit: props?.limit ?? 6,
    };

    if (preset.value) {
        res.preset = preset.value;
    }

    if (artist.value) {
        res.artist = artist.value;
    }

    if (curator.value) {
        res.curator = curator.value;
    }

    if (discipline.value) {
        res.discipline = discipline.value;
    }

    if (events.value) {
        res.events = events.value;
    }

    if (productionHouse.value) {
        res.productionHouse = productionHouse.value;
    }

    if (purview.value) {
        res.purview = purview.value;
    }

    if (relatedToArtist.value) {
        res.relatedToArtist = relatedToArtist.value;
    }

    if (relatedToVenue.value) {
        res.relatedToVenue = relatedToVenue.value;
    }

    if (venue.value) {
        res.venue = venue.value;
    }

    if (search.value) {
        res.q = search.value;
    }

    if (skip.value) {
        res.skip = skip.value;
    }

    if (tag.value) {
        res.tag = tag.value;
    }

    return res;
});

const { data, status, refresh } = await useAsyncFetchLaravel<QueryBundleResponse>(ENDPOINTS[props.bundleType], {
    query,
    lazy: props.lazy,
});

const loadMore = () => {
    currentPage.value += 1;
};

const errorCount = ref(0);

watch(query, (newQuery, oldQuery) => {
    if (newQuery.page === oldQuery.page) {
        currentPage.value = 1;
    }
});

watch(status, async() => {
    if (status.value === 'error') {
        errorCount.value += 1;
        if (errorCount.value === 0) {
            await refresh();
        }
    } else if (status.value === 'success') {
        errorCount.value = 0;
    }
});

watchEffect(() => {
    if (status.value !=='success') {
        return;
    }

    if (data.value && 'meta' in data.value) {
        if (data.value.meta?.total) {
            totalResults.value = data.value.meta.total;
        }

        if (data.value.meta?.last_page) {
            lastPageNumber.value = data.value.meta.last_page;
        }
    }

    if (data.value && 'data' in data.value) {
        if (currentPage.value > 1 && Array.isArray(data.value.data) && Array.isArray(bundleResult.value)) {
            bundleResult.value.push(...data.value.data);
        } else {
            bundleResult.value = data.value.data;
        }
    }
});
</script>
